import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { EmptyState } from './analyticsHelpers';

// ─── Canvas-based heatmap via Google Maps OverlayView ────────────────────────
// Pure HTML5 canvas — no HeatmapLayer dependency, no v3.65 deprecation risk.

class CanvasHeatmapOverlay {
    constructor() {
        this._points = [];
        this._radius = 25;
        this._opacity = 0.75;
        this._div = null;
        this._canvas = null;
        this._ctx = null;
        this._map = null;
        this._overlay = null;
    }

    // Build the OverlayView and mount it onto the given Google Map instance
    mount(googleMap) {
        this._map = googleMap;

        const overlay = new window.google.maps.OverlayView();
        this._overlay = overlay;

        overlay.onAdd = () => {
            const div = document.createElement('div');
            div.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;';
            const canvas = document.createElement('canvas');
            canvas.style.cssText = 'position:absolute;top:0;left:0;';
            div.appendChild(canvas);
            this._div = div;
            this._canvas = canvas;
            this._ctx = canvas.getContext('2d');
            overlay.getPanes().overlayLayer.appendChild(div);
        };

        overlay.draw = () => {
            if (!this._canvas || !this._map) return;
            const projection = overlay.getProjection();
            if (!projection) return;

            // Size canvas to the map container
            const mapDiv = googleMap.getDiv();
            const w = mapDiv.offsetWidth;
            const h = mapDiv.offsetHeight;
            this._canvas.width = w;
            this._canvas.height = h;
            this._canvas.style.width = w + 'px';
            this._canvas.style.height = h + 'px';

            // Reposition the overlay div relative to the map pane origin
            const bounds = googleMap.getBounds();
            if (!bounds) return;

            const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
            const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
            if (!ne || !sw) return;

            this._div.style.left = Math.min(ne.x, sw.x) + 'px';
            this._div.style.top = Math.min(ne.y, sw.y) + 'px';

            this._render(projection, w, h);
        };

        overlay.onRemove = () => {
            if (this._div) {
                this._div.parentNode?.removeChild(this._div);
                this._div = null;
                this._canvas = null;
                this._ctx = null;
            }
        };

        overlay.setMap(googleMap);
    }

    setPoints(points) {
        this._points = points || [];
        if (this._overlay) {
            try { this._overlay.draw(); } catch (_) { /* map not ready */ }
        }
    }

    setRadius(r) { this._radius = r; }
    setOpacity(o) { this._opacity = o; }

    _render(projection, w, h) {
        const ctx = this._ctx;
        if (!ctx) return;
        ctx.clearRect(0, 0, w, h);
        if (this._points.length === 0) return;

        // Accumulation pass — draw radial gradients per point onto an offscreen canvas
        const offscreen = document.createElement('canvas');
        offscreen.width = w;
        offscreen.height = h;
        const octx = offscreen.getContext('2d');

        this._points.forEach(({ lat, lng }) => {
            const px = projection.fromLatLngToDivPixel(
                new window.google.maps.LatLng(lat, lng)
            );
            if (!px) return;

            // Adjust pixel coords relative to the div offset
            const divLeft = parseFloat(this._div?.style.left || 0);
            const divTop = parseFloat(this._div?.style.top || 0);
            const x = px.x - divLeft;
            const y = px.y - divTop;

            if (x < -this._radius || y < -this._radius || x > w + this._radius || y > h + this._radius) return;

            const grad = octx.createRadialGradient(x, y, 0, x, y, this._radius);
            grad.addColorStop(0, 'rgba(0,0,0,0.25)');
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            octx.fillStyle = grad;
            octx.fillRect(x - this._radius, y - this._radius, this._radius * 2, this._radius * 2);
        });

        // Colorize pass — map alpha values to a heat gradient
        const imgData = octx.getImageData(0, 0, w, h);
        const colorized = ctx.createImageData(w, h);
        const palette = this._buildPalette();

        for (let i = 0; i < imgData.data.length; i += 4) {
            const alpha = imgData.data[i + 3];
            if (alpha === 0) continue;
            const t = Math.min(Math.floor((alpha / 255) * (palette.length / 4 - 1)) * 4, palette.length - 4);
            colorized.data[i]     = palette[t];
            colorized.data[i + 1] = palette[t + 1];
            colorized.data[i + 2] = palette[t + 2];
            colorized.data[i + 3] = Math.round(alpha * this._opacity);
        }
        ctx.putImageData(colorized, 0, 0);
    }

    // Build a 256-stop palette: blue → cyan → green → yellow → red
    _buildPalette() {
        const stops = [
            [0,     [0,   0,   255]],
            [0.25,  [0,   200, 200]],
            [0.5,   [0,   200, 0]  ],
            [0.75,  [255, 200, 0]  ],
            [1.0,   [255, 0,   0]  ],
        ];
        const size = 256;
        const palette = new Uint8Array(size * 4);
        for (let i = 0; i < size; i++) {
            const t = i / (size - 1);
            let c = stops[0][1];
            for (let j = 1; j < stops.length; j++) {
                if (t <= stops[j][0]) {
                    const prev = stops[j - 1];
                    const next = stops[j];
                    const f = (t - prev[0]) / (next[0] - prev[0]);
                    c = prev[1].map((v, k) => Math.round(v + f * (next[1][k] - v)));
                    break;
                }
            }
            palette[i * 4]     = c[0];
            palette[i * 4 + 1] = c[1];
            palette[i * 4 + 2] = c[2];
            palette[i * 4 + 3] = 255;
        }
        return palette;
    }

    destroy() {
        if (this._overlay) {
            this._overlay.setMap(null);
            this._overlay = null;
        }
    }
}

// ─── Time window slider ───────────────────────────────────────────────────────
const TimeWindowSlider = ({ pings, windowMinutes, onWindowChange, windowStart, onWindowStartChange }) => {
    if (!pings || pings.length === 0) return null;

    const firstTs = new Date(pings[0].timestamp).getTime();
    const lastTs = new Date(pings[pings.length - 1].timestamp).getTime();
    const totalMinutes = Math.ceil((lastTs - firstTs) / 60000);

    if (totalMinutes <= windowMinutes) return null;

    const maxStart = totalMinutes - windowMinutes;
    const windowEnd = windowStart + windowMinutes;

    const formatMinutes = (m) => {
        const d = new Date(firstTs + m * 60000);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="time-slider-wrap">
            <div className="time-slider-header">
                <span className="time-slider-label">Time Window</span>
                <span className="time-slider-range">
                    {formatMinutes(windowStart)} – {formatMinutes(windowEnd)}
                    <span className="time-slider-size"> ({windowMinutes} min)</span>
                </span>
            </div>
            <input
                type="range"
                min={0}
                max={maxStart}
                value={windowStart}
                onChange={e => onWindowStartChange(Number(e.target.value))}
                className="time-slider"
            />
            <div className="time-slider-controls">
                <span className="time-slider-edge">{formatMinutes(0)}</span>
                <div className="window-size-pills">
                    {[5, 10, 20, 60].map(w => (
                        <button
                            key={w}
                            className={`window-pill ${windowMinutes === w ? 'active' : ''}`}
                            onClick={() => onWindowChange(w)}
                        >
                            {w}m
                        </button>
                    ))}
                    <button
                        className={`window-pill ${windowMinutes === totalMinutes ? 'active' : ''}`}
                        onClick={() => { onWindowChange(totalMinutes); onWindowStartChange(0); }}
                    >
                        All
                    </button>
                </div>
                <span className="time-slider-edge">{formatMinutes(maxStart)}</span>
            </div>
        </div>
    );
};

// ─── HeatmapTab ───────────────────────────────────────────────────────────────
const HeatmapTab = ({ mapData, pings, isActive, isVisible }) => {
    const mapRef = useRef(null);
    const googleMapRef = useRef(null);
    const overlayRef = useRef(null);
    const mountedRef = useRef(false);

    const [windowMinutes, setWindowMinutes] = useState(10);
    const [windowStart, setWindowStart] = useState(0);
    const [radius, setRadius] = useState(25);

    // ── Filter pings to the selected time window ──
    const filteredPings = useMemo(() => {
        if (!pings || pings.length === 0) return [];
        if (!pings[0]?.timestamp) return pings;

        const firstTs = new Date(pings[0].timestamp).getTime();
        const windowStartMs = windowStart * 60000;
        const windowEndMs = (windowStart + windowMinutes) * 60000;

        const totalMs = new Date(pings[pings.length - 1].timestamp).getTime() - firstTs;
        if (totalMs <= windowMinutes * 60000) return pings; // fits in window — show all

        return pings.filter(p => {
            const offset = new Date(p.timestamp).getTime() - firstTs;
            return offset >= windowStartMs && offset <= windowEndMs;
        });
    }, [pings, windowMinutes, windowStart]);

    // ── Mount Google Map once ──
    useEffect(() => {
        if (mountedRef.current) return;
        if (!mapRef.current || !window.google || !mapData) return;

        const center = mapData.area?.center
            ? { lat: mapData.area.center.lat, lng: mapData.area.center.lng }
            : { lat: 32.0858, lng: 34.7823 };

        const gmap = new window.google.maps.Map(mapRef.current, {
            center,
            zoom: 17,
            mapTypeId: 'roadmap',
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            styles: [
                { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
            ],
        });

        googleMapRef.current = gmap;

        // Draw venue polygon
        if (mapData.area?.coordinates?.length > 0) {
            const path = mapData.area.coordinates.map(c => ({ lat: c[1], lng: c[0] }));
            new window.google.maps.Polygon({
                paths: path,
                strokeColor: '#2B8EE8',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#2B8EE8',
                fillOpacity: 0.06,
                map: gmap,
            });
        }

        // Mount canvas heatmap overlay
        const heatmap = new CanvasHeatmapOverlay();
        heatmap.mount(gmap);
        overlayRef.current = heatmap;

        mountedRef.current = true;
    }, [mapData]);

    // ── Update heatmap points when filtered pings or radius change ──
    useEffect(() => {
        if (!overlayRef.current) return;
        overlayRef.current.setRadius(radius);
        overlayRef.current.setPoints(filteredPings);
    }, [filteredPings, radius]);

    // ── Trigger map resize when tab becomes visible ──
    useEffect(() => {
        if (isVisible && googleMapRef.current) {
            window.google?.maps?.event?.trigger(googleMapRef.current, 'resize');
        }
    }, [isVisible]);

    const hasPings = pings && pings.length > 0;

    return (
        <div className="ad-tab-content">
            <section className="ad-section">
                <div className="ad-card ad-card-fullwidth">
                    <div className="ad-card-header">
                        <div>
                            <h3 className="ad-card-title" style={{ marginBottom: 0 }}>
                                Attendee Location Heatmap
                            </h3>
                            <p className="ad-card-sub" style={{ marginTop: '0.25rem', marginBottom: 0 }}>
                                GPS pings collected with &lt;50m accuracy
                            </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span className="ping-count">
                                <strong>{filteredPings.length}</strong> of {pings?.length || 0} pings
                            </span>
                            {isActive && (
                                <div className="live-badge">
                                    <span className="live-dot" /> LIVE
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Time window slider */}
                    <TimeWindowSlider
                        pings={pings}
                        windowMinutes={windowMinutes}
                        windowStart={windowStart}
                        onWindowChange={setWindowMinutes}
                        onWindowStartChange={setWindowStart}
                    />

                    {/* Radius control */}
                    <div className="heatmap-controls">
                        <label className="heatmap-control-label">
                            Radius
                            <input
                                type="range"
                                min={10}
                                max={60}
                                value={radius}
                                onChange={e => setRadius(Number(e.target.value))}
                                className="radius-slider"
                            />
                            <span>{radius}px</span>
                        </label>
                    </div>

                    {/* Map container — NEVER unmounted, controlled by isVisible CSS in parent */}
                    <div className="heatmap-panel">
                        {!hasPings && (
                            <div className="heatmap-overlay-msg">
                                No GPS data — attendees need to grant location permission
                            </div>
                        )}
                        <div ref={mapRef} className="heatmap-map" />
                    </div>

                </div>
            </section>
        </div>
    );
};

export default HeatmapTab;